import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useSeo } from "@/hooks/useSeo";
import FaqSection from "@/components/feature/FaqSection";

const heroImg = "/images/equipment.jpeg";

const serviceImages = [
  { src: "/images/GRANSTARK-staff2.jpeg", alt: "交通誘導を行うGRANSTARKの警備スタッフ" },
  { src: "/images/GRANSTARK-staff1.jpeg", alt: "高速道路警備を担当するGRANSTARKのスタッフ" },
  { src: "/images/GRANSTARK_event(businesss.html).jpg", alt: "GRANSTARKのイベント・雑踏警備の現場" },
  { src: "/images/GRANSTARK_facility(businesss.html).jpg", alt: "施設警備を行うGRANSTARKのスタッフ" },
];

const services = [
  {
    title: "交通誘導",
    desc: "工事現場等において通行人や地域住民の方々、作業員の皆様など、あらゆる人員の安全を確保し、作業のスムーズな進行を実現すべく、的確な案内・誘導業務を実施しています。",
  },
  {
    title: "高速道路",
    desc: "当社の事業の大きな柱の一つが、高速道路での警備です。高速道路での業務は、管理者の厳しい安全基準を満たす必要があり、新規参入が非常に難しい専門領域です。私たちはその高いハードルをクリアし、高速道路上での工事を安全に進めるための車線規制や誘導といった特殊な業務を担っています。危険が伴うからこそ、社会の重要インフラを守っているという強い誇りと、都市開発の一端を担うやりがいを感じられる仕事です。",
  },
  {
    title: "イベント警備（雑踏警備）",
    desc: "マラソン大会や地元で有名な祭りといった大規模なものから音楽フェス、格闘技イベント、地域のグルメイベントまで、豊富な実績があります。イベント警備は、ただ立っているだけではありません。多くの来場者が集まる中で、状況は流動的に変化します。そのため、自ら考えて臨機応変に対応する力と仲間とコミュニケーションを取り合って動く団結力が何よりも求められます。今後は、警備だけでなく、イベントの運営管理まで担える体制を目指しています。",
  },
  {
    title: "施設警備",
    desc: "マンションやビル・様々な施設の恒久的な防犯・防災のため、教育を受けた専門の警備スタッフによる警戒・監視・巡回業務を実施しています。",
  },
];

const businessFaq = [
  {
    question: "GRANSTARKはどんな警備会社ですか？",
    answer: "株式会社GRANSTARKは2019年創業の大阪密着の警備会社です。大阪府公安委員会認定（第62003216号）を取得しており、交通誘導・高速道路・イベント（雑踏）・施設警備の4分野に対応しています。",
  },
  {
    question: "GRANSTARKに警備を依頼するにはどうすればいいですか？",
    answer: "お電話（06-6786-9252）またはホームページのお問い合わせフォームからご連絡ください。最短即日でご対応いたします。",
  },
  {
    question: "対応エリアはどこですか？",
    answer: "主に大阪府内全域に対応しています。",
  },
];

export default function Business() {
  useSeo({
    title: "事業内容｜交通誘導・イベント・高速道路警備｜株式会社GRANSTARK",
    description:
      "GRANSTARKの警備サービス一覧。交通誘導警備・雑踏警備・イベント警備・高速道路警備・施設警備に対応。大阪府公安委員会認定の警備会社として大阪・関西全域をサポートします。",
    ogUrl: "https://www.gs-granstark.com/business",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": "https://www.gs-granstark.com/business#webpage",
        url: "https://www.gs-granstark.com/business",
        name: "事業内容｜交通誘導・イベント・高速道路警備｜株式会社GRANSTARK",
        description:
          "GRANSTARKの警備サービス一覧。交通誘導警備・雑踏警備・イベント警備・高速道路警備・施設警備に対応。大阪府公安委員会認定の警備会社として大阪・関西全域をサポートします。",
        isPartOf: { "@id": "https://www.gs-granstark.com/#website" },
        publisher: { "@id": "https://www.gs-granstark.com/#business" },
        inLanguage: "ja",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: businessFaq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
    ],
  });

  const { ref: heroRef, isVisible: heroVisible } = useScrollReveal<HTMLDivElement>(0.1);
  const { ref: ref1, isVisible: v1 } = useScrollReveal<HTMLDivElement>(0.15, 100);
  const { ref: ref2, isVisible: v2 } = useScrollReveal<HTMLDivElement>(0.15, 100);
  const { ref: ref3, isVisible: v3 } = useScrollReveal<HTMLDivElement>(0.15, 100);
  const { ref: ref4, isVisible: v4 } = useScrollReveal<HTMLDivElement>(0.15, 100);
  const { ref: introRef, isVisible: introVisible } = useScrollReveal<HTMLDivElement>(0.15);

  const serviceRefs = [ref1, ref2, ref3, ref4];
  const serviceVis = [v1, v2, v3, v4];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[420px] md:h-[520px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
          style={{ backgroundImage: `url('${heroImg}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/50 via-navy/60 to-navy/80 pointer-events-none" />
        <div
          ref={heroRef}
          className={`relative z-10 h-full flex items-center justify-center transition-all duration-1000 ${
            heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="text-center px-4">
            <span className="text-gold text-sm tracking-[4px] font-bold block mb-3">BUSINESS</span>
            <h1 className="text-4xl md:text-5xl font-black text-white">事業内容</h1>
            <p className="text-gray-300 mt-4 text-lg">business</p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-16 md:py-24">
        <div
          ref={introRef}
          className={`w-full px-4 md:px-8 max-w-4xl mx-auto text-center transition-all duration-1000 ${
            introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-8 leading-snug">
            大阪の発展を支える、誇りある警備
          </h2>
          <p className="text-base md:text-lg text-gray-700 leading-[1.9]">
            私たちは、大阪府公安委員会より正式な認定を受けた警備会社。平日であれば、街のインフラを支える土木・通信・ガス工事などの警備を行います。それに加え、私たちはあえて専門性が求められる2つの分野に力を入れています。それが【高速道路】と【イベント】の警備。高速道路は社会インフラを守るプライドを、イベントは多くの来場者の安全を守るやりがいを感じられます。私たちは、やりがいと収入の両方を実現できる環境を目指しています。
          </p>
        </div>
      </section>

      {/* Service Detail */}
      <section id="service-detail" className="bg-gray-50 py-16 md:py-24">
        <div className="w-full px-4 md:px-8 max-w-6xl mx-auto space-y-20 md:space-y-28">
          {services.map((service, i) => (
            <div
              key={service.title}
              ref={serviceRefs[i]}
              className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center transition-all duration-700 ${
                serviceVis[i] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <div className={`${i % 2 === 1 ? "md:order-2" : ""}`}>
                <div className="h-64 md:h-80 rounded-2xl overflow-hidden bg-gray-100">
                  <img
                    src={serviceImages[i].src}
                    alt={serviceImages[i].alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className={`${i % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="w-14 h-14 rounded-full bg-[#f5ecd7] flex items-center justify-center mb-5">
                  <span className="w-8 h-8 flex items-center justify-center text-navy">
                    <i className={`${["ri-traffic-light-line", "ri-roadster-line", "ri-fire-line", "ri-building-4-line"][i]} text-2xl`}></i>
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-navy mb-5">{service.title}</h3>
                <p className="text-gray-700 leading-[1.9]">{service.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-16 md:py-24">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            警備のご依頼・お見積もりはこちら
          </h2>
          <p className="text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            まずはお電話またはメールフォームよりご連絡ください。最短即日でご対応いたします。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 bg-[#E47911] text-white font-bold text-sm md:text-lg px-6 md:px-10 py-4 md:py-5 rounded-full transition-all duration-300 hover:bg-[#E47911]/90 hover:scale-105 max-w-full"
          >
            <span className="whitespace-nowrap">警備のご依頼・お見積もりはこちら</span>
            <span className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-right-line"></i>
            </span>
          </Link>
        </div>
      </section>

      <FaqSection items={businessFaq} bgColor="bg-white" />
    </div>
  );
}
